//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Paper, Tab } from '@mui/material'
import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement } from 'react'
import { useParams } from 'react-router-dom'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

import { OtStoreState, useOtSelector } from '@react/redux/store'
import PoolOverview from '@react/lender/PoolOverview'
import LoanList from './loans/LoanList'
import PoolAssets from './pools/PoolAssets'
import PoolContractAddresses from './pools/PoolContractAddresses'
import { PoolFlexCalendar } from './pools/PoolFlexCalendat'
import PoolLegalPage from './pools/PoolLegalPage'
import PoolSummary from './pools/PoolSummary'

enum PoolTabEnum {
  OVERVIEW = 'OVERVIEW',
  HOLDINGS = 'HOLDINGS',
  ACCESS_LIST = 'ACCESS_LIST',
  DEPOSITS = 'DEPOSITS',
  WITHDRAWS = 'WITHDRAWS',
  LENDER_VIEW = 'LENDER_VIEW',
  SCHEDULE = 'SCHEDULE',
  LEGAL = 'LEGAL',
  ASSETS = 'ASSETS',
  LOANS = 'LOANS',
  WITHDRAW_EVENTS = 'WITHDRAW_EVENTS',
  CONTRACT_ADDRESSES = 'CONTRACT_ADDRESSES',
  FLEX_CALENDAR = 'FLEX_CALENDAR',
  OFFCHAIN_DATA = 'OFFCHAIN_DATA',
  REPORTS = 'REPORTS',
}
export default function UnconnectedPoolDetails(props: { poolAddr?: EthAddr }): ReactElement {
  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr

  const [selectedTab, setSelectedTab] = React.useState<PoolTabEnum>(PoolTabEnum.OVERVIEW)
  const handleChange = (_event: React.SyntheticEvent, newValue: PoolTabEnum): void => {
    setSelectedTab(newValue)
  }
  const poolBaseOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  )

  if (poolAddr) {
    return (
      <Box>
        <PoolSummary poolAddr={poolAddr} />
        <Paper sx={{ marginTop: 4, marginBottom: 4 }}>
          <Box sx={{ width: '100%' }}>
            <TabContext value={selectedTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={'Overview'} value={PoolTabEnum.OVERVIEW} />
                  {poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term && (
                    <Tab label={'Loans'} value={PoolTabEnum.LOANS} />
                  )}
                  <Tab label={'Collateral'} value={PoolTabEnum.ASSETS} />
                  <Tab label={'Info'} value={PoolTabEnum.LEGAL} />
                  <Tab label={'Contract Addresses'} value={PoolTabEnum.CONTRACT_ADDRESSES} />
                  {poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate && (
                    <Tab label={'Calendar'} value={PoolTabEnum.FLEX_CALENDAR} />
                  )}
                </TabList>
              </Box>

              <TabPanel value={PoolTabEnum.OVERVIEW}>
                <PoolOverview />
              </TabPanel>
              <TabPanel value={PoolTabEnum.LEGAL}>
                <PoolLegalPage />
              </TabPanel>
              {poolBaseOverviewCTO?.poolType == PoolTypeEnum.Term && (
                <TabPanel value={PoolTabEnum.LOANS}>
                  <LoanList />
                </TabPanel>
              )}
              <TabPanel value={PoolTabEnum.ASSETS}>
                <PoolAssets />
              </TabPanel>
              <TabPanel value={PoolTabEnum.CONTRACT_ADDRESSES}>
                <PoolContractAddresses role={RoleEnum.LENDER} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.FLEX_CALENDAR}>
                <PoolFlexCalendar poolAddr={poolAddr} role={RoleEnum.LENDER} />
              </TabPanel>
            </TabContext>
          </Box>
        </Paper>
      </Box>
    )
  } else {
    return <></>
  }
}
